import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';

// Components for Admin Dashboard Sections
import GenerateQrCode from '../components/admin/GenerateQrCode';
import UploadImages from '../components/admin/UploadImages';
import ManageVisitors from '../components/admin/ManageVisitors';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Sidebar = styled.aside`
  width: 280px;
  background-color: var(--paper-color);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 10;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  
  [data-theme='dark'] & {
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 70px;
    padding: 20px 0;
  }
`;

const MainContent = styled.main`
  flex-grow: 1;
  padding: 30px;
  background-color: var(--background-color);
  margin-left: 280px;
  min-height: 100vh;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 70px;
    padding: 20px;
  }
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: var(--primary-color);
  padding: 25px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    padding: 20px 10px;
  }
`;

const LogoAccent = styled.span`
  color: var(--secondary-color);
`;

const LogoText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: 5px;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-primary)'};
  text-decoration: none;
  border-radius: 0;
  background-color: ${props => props.$active ? 'rgba(67, 97, 238, 0.1)' : 'transparent'};
  font-weight: ${props => props.$active ? '600' : '500'};
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: rgba(67, 97, 238, 0.05);
    color: var(--primary-color);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: var(--primary-color);
    transform: ${props => props.$active ? 'scaleY(1)' : 'scaleY(0)'};
    transition: transform 0.2s;
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  @media (max-width: 768px) {
    padding: 15px 0;
    justify-content: center;
    
    &::before {
      display: none;
    }
  }
`;

const NavText = styled.span`
  margin-left: 12px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavIcon = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  width: calc(100% - 40px);
  padding: 15px 20px;
  margin: 10px auto;
  background-color: rgba(239, 71, 111, 0.1);
  color: var(--error-color);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  font-size: 15px;
  font-family: 'Poppins', sans-serif;
  
  &:hover {
    background-color: rgba(239, 71, 111, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
    margin: 10px auto;
  }
`;

const LogoutText = styled.span`
  margin-left: 12px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ThemeToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px auto;
  padding: 8px;
  width: calc(100% - 40px);
  border-radius: 8px;
  background-color: var(--paper-color);
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
`;

// Icon components
const DashboardIcon = () => (
  <NavIcon>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor"/>
    </svg>
  </NavIcon>
);

const QrCodeIcon = () => (
  <NavIcon>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 11H11V3H3V11ZM5 5H9V9H5V5Z" fill="currentColor"/>
      <path d="M3 21H11V13H3V21ZM5 15H9V19H5V15Z" fill="currentColor"/>
      <path d="M13 3V11H21V3H13ZM19 9H15V5H19V9Z" fill="currentColor"/>
      <path d="M21 19H19V21H21V19Z" fill="currentColor"/>
      <path d="M15 13H13V15H15V13Z" fill="currentColor"/>
      <path d="M17 15H15V17H17V15Z" fill="currentColor"/>
      <path d="M15 17H13V19H15V17Z" fill="currentColor"/>
      <path d="M17 19H15V21H17V19Z" fill="currentColor"/>
      <path d="M19 17H17V19H19V17Z" fill="currentColor"/>
      <path d="M19 13H17V15H19V13Z" fill="currentColor"/>
      <path d="M21 15H19V17H21V15Z" fill="currentColor"/>
    </svg>
  </NavIcon>
);

const UploadIcon = () => (
  <NavIcon>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM14 13V17H10V13H7L12 8L17 13H14Z" fill="currentColor"/>
    </svg>
  </NavIcon>
);

const UsersIcon = () => (
  <NavIcon>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="currentColor"/>
    </svg>
  </NavIcon>
);

const LogoutIcon = () => (
  <NavIcon>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
    </svg>
  </NavIcon>
);

const AdminDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const isActive = (path: string) => {
    return location.pathname.includes(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <DashboardContainer>
      <Sidebar>
        <Logo>
          <LogoText>Me<LogoAccent>Studio</LogoAccent></LogoText>
        </Logo>
        <NavMenu>
          {/* <NavItem>
            <NavLink to="/admin/dashboard" $active={isActive('/dashboard')}>
              <DashboardIcon />
              <NavText>Dashboard</NavText>
            </NavLink>
          </NavItem> */}
          <NavItem>
            <NavLink to="/admin/generate-qr" $active={isActive('/generate-qr')}>
              <QrCodeIcon />
              <NavText>Generate QR</NavText>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/upload-images" $active={isActive('/upload-images')}>
              <UploadIcon />
              <NavText>Upload Images</NavText>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/manage-visitors" $active={isActive('/manage-visitors')}>
              <UsersIcon />
              <NavText>Manage Visitors</NavText>
            </NavLink>
          </NavItem>
        </NavMenu>
        <ThemeToggleContainer>
          <ThemeToggle />
        </ThemeToggleContainer>
        <LogoutButton onClick={handleLogout}>
          <LogoutIcon />
          <LogoutText>Logout</LogoutText>
        </LogoutButton>
      </Sidebar>

      <MainContent>
        <Routes>
          {/* <Route path="/dashboard" element={
            <>
              <Header>
                <PageTitle>Dashboard Overview</PageTitle>
              </Header>
              <div style={{ animation: 'slideUp 0.5s ease' }}>
                <WelcomeCard />
                <StatCards />
              </div>
            </>
          } /> */}
          <Route path="/generate-qr" element={<GenerateQrCode />} />
          <Route path="/upload-images" element={<UploadImages />} />
          <Route path="/manage-visitors" element={<ManageVisitors />} />
          <Route path="*" element={<Navigate to="/admin/generate-qr" replace />} />
        </Routes>
      </MainContent>
    </DashboardContainer>
  );
};

// Dashboard components
const WelcomeCard = () => {
  const currentHour = new Date().getHours();
  let greeting = 'Good morning';
  
  if (currentHour >= 12 && currentHour < 18) {
    greeting = 'Good afternoon';
  } else if (currentHour >= 18) {
    greeting = 'Good evening';
  }

  return (
    <WelcomeContainer>
      <WelcomeText>
        <WelcomeHeading>{greeting}, Admin! 👋</WelcomeHeading>
        <WelcomeSubtext>
          Welcome to your Image Share dashboard. Here's what's happening with your event photos today.
        </WelcomeSubtext>
      </WelcomeText>
    </WelcomeContainer>
  );
};

const WelcomeContainer = styled.div`
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  padding: 30px;
  border-radius: var(--border-radius);
  color: white;
  margin-bottom: 30px;
  box-shadow: var(--box-shadow);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WelcomeText = styled.div`
  flex: 1;
`;

const WelcomeHeading = styled.h2`
  margin: 0 0 10px 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const WelcomeSubtext = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
  max-width: 600px;
`;

const StatCards = () => {
  // In a real app, these would be fetched from the server
  const stats = [
    { title: 'Total Visitors', value: '24', icon: '👥', color: '#4361ee' },
    { title: 'Total Images', value: '128', icon: '🖼️', color: '#3a0ca3' },
    { title: 'Today\'s Views', value: '32', icon: '👁️', color: '#f72585' }
  ];

  return (
    <StatCardGrid>
      {stats.map((stat, index) => (
        <StatCard key={index} style={{ animationDelay: `${index * 0.1}s` }}>
          <StatIcon style={{ backgroundColor: stat.color }}>{stat.icon}</StatIcon>
          <StatContent>
            <StatValue>{stat.value}</StatValue>
            <StatTitle>{stat.title}</StatTitle>
          </StatContent>
        </StatCard>
      ))}
    </StatCardGrid>
  );
};

const StatCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: var(--box-shadow);
  transition: transform 0.3s ease, background-color var(--transition-speed);
  animation: slideInRight 0.5s ease both;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-right: 15px;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 5px;
`;

const StatTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

export default AdminDashboard;
